import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';

import CardDesignerCard from '../CardDesigner/components/CardDesignerCard';
import DesignEditor from '../DesignEditor';
import DesignEditorHeader, {
  DesignEditorHeaderCenterSlot,
  DesignEditorHeaderLeftSlot,
  DesignEditorHeaderRightSlot,
} from '../DesignEditor/components/DesignEditorHeader';
import Button from '../Button';
import useCardDesigner from '../CardDesigner/useCardDesigner';
import { CARD_SIDE } from '../../../services/cards/constants';
import { getCardSideElementsConfiguration } from '../CardDesigner/helpers';
import CollageDesignerControlPanel from './components/CollageDesignerControlPanel';
import DesignEditorPreviewPane, {
  DesignEditorPreviewContent,
} from '../DesignEditor/components/DesignEditorPreviewPane';
import { isElementCardBackground } from '../CardDesigner/components/CardDesignerControlPanel/components/CardDesignerBackgroundControls/helpers';
import Angle from '../Angle';

const propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  onComplete: PropTypes.func,
  defaultCardId: PropTypes.string,
  title: PropTypes.string,
};

const CollageDesigner = ({
  className,
  onComplete,
  onClose,
  defaultCardId,
  title = 'Creating a collage',
}) => {
  const classes = classNames({
    [className]: !!className,
    'custom-print-card-designer': true,
  });

  const { cardId = defaultCardId } = useParams();
  const {
    card,
    cardSide,
    onCardElementCreate,
    onCardSettingsUpdate,
    selectedCardElementId,
    onCardElementSelect,
    onCardElementUpdate,
    onElementConfigurationChange,
  } = useCardDesigner({
    defaultCardId: cardId,
    side: CARD_SIDE.FRONT,
  });

  if (!cardSide) {
    return null;
  }

  const elements = getCardSideElementsConfiguration(cardSide);
  const backgroundElement = elements.find(element =>
    isElementCardBackground(element),
  );

  const handleComplete = async () => {
    await onComplete({
      card,
    });
  };

  return (
    <DesignEditor className={classes}>
      <DesignEditorHeader>
        <DesignEditorHeaderLeftSlot>
          {onClose && (
            <Button btnStyle="link" className="text-bold" onClick={onClose}>
              <Angle direction="left" />
              Back
            </Button>
          )}
        </DesignEditorHeaderLeftSlot>

        <DesignEditorHeaderCenterSlot>
          <h3 className="design-editor-header-title">{title}</h3>
        </DesignEditorHeaderCenterSlot>

        <DesignEditorHeaderRightSlot>
          <Button
            btnSize="sm"
            onClick={() => {
              handleComplete();
              onClose?.();
            }}
          >
            Save and share
          </Button>
        </DesignEditorHeaderRightSlot>
      </DesignEditorHeader>

      <CollageDesignerControlPanel
        onCardSettingsUpdate={onCardSettingsUpdate}
        onCardElementCreate={onCardElementCreate}
        card={card}
        onElementConfigurationChange={onElementConfigurationChange}
        backgroundElement={backgroundElement}
      />

      <DesignEditorPreviewPane>
        <DesignEditorPreviewContent>
          {card && cardSide && (
            <CardDesignerCard
              card={card}
              cardSide={cardSide}
              selectedCardElementId={selectedCardElementId}
              onCardElementSelect={onCardElementSelect}
              onCardElementUpdate={onCardElementUpdate}
              onElementConfigurationChange={onElementConfigurationChange}
            />
          )}
        </DesignEditorPreviewContent>
      </DesignEditorPreviewPane>
    </DesignEditor>
  );
};

CollageDesigner.propTypes = propTypes;

export default CollageDesigner;
